import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eiendomAPI, bygningAPI, romAPI, bygningsdelAPI, rapportAPI } from '../services/api';
import BildeOpplaster from '../components/BildeOpplaster';
import BildeGalleri from '../components/BildeGalleri';
import './Eiendom.css';

function Eiendom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [eiendom, setEiendom] = useState(null);
  const [bygninger, setBygninger] = useState([]);
  const [eiendomBilder, setEiendomBilder] = useState([]);
  const [bygningBilder, setBygningBilder] = useState({}); // { bygningId: [bilder] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNyBygning, setShowNyBygning] = useState(false);
  const [showNyRom, setShowNyRom] = useState(null); // bygning_id når vi oppretter rom
  const [bygningForm, setBygningForm] = useState({
    bygning_type: 'hovedbygg',
    bygning_navn: '',
    byggeaar: '',
    bra: '',
    bra_i: '',
    bra_e: '',
    bra_b: '',
    tba: '',
    gua: '',
    alh: '',
    etasjer: 1,
  });
  const [romForm, setRomForm] = useState({
    romtype: 'bad',
    romnummer: 1,
    etasje: 'etasje_1',
    tilstandsgrad: 'TG1',
    renoverings_aar: '',
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const eiendomRes = await eiendomAPI.getById(id);

      setEiendom(eiendomRes.data);

      // Backend returnerer allerede bygninger med rom
      setBygninger(eiendomRes.data.bygninger || []);

      // Hent eiendomsbilder
      fetchEiendomBilder();

      // Hent bilder for hver bygning
      if (eiendomRes.data.bygninger) {
        eiendomRes.data.bygninger.forEach(bygning => {
          fetchBygningBilder(bygning.id);
        });
      }
    } catch (err) {
      setError('Kunne ikke hente eiendomsdata: ' + (err.response?.data?.error || err.message));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEiendomBilder = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/api/eiendom/${id}/bilder`, { headers });
      if (response.ok) {
        const bilder = await response.json();
        setEiendomBilder(bilder);
      }
    } catch (error) {
      console.error('Feil ved henting av eiendomsbilder:', error);
    }
  };

  const fetchBygningBilder = async (bygningId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/api/bygning/${bygningId}/bilder`, { headers });
      if (response.ok) {
        const bilder = await response.json();
        setBygningBilder(prev => ({ ...prev, [bygningId]: bilder }));
      }
    } catch (error) {
      console.error('Feil ved henting av bygningsbilder:', error);
    }
  };

  const handleBygningSubmit = async (e) => {
    e.preventDefault();

    // Valider arealberegninger
    const bra_i = bygningForm.bra_i ? parseInt(bygningForm.bra_i) : 0;
    const bra_e = bygningForm.bra_e ? parseInt(bygningForm.bra_e) : 0;
    const bra_b = bygningForm.bra_b ? parseInt(bygningForm.bra_b) : 0;
    const bra = bygningForm.bra ? parseInt(bygningForm.bra) : 0;
    const alh = bygningForm.alh ? parseInt(bygningForm.alh) : 0;
    const gua = bygningForm.gua ? parseInt(bygningForm.gua) : 0;

    // Sjekk BRA = BRA-i + BRA-e + BRA-b
    if ((bra_i || bra_e || bra_b) && bra) {
      const beregnet_bra = bra_i + bra_e + bra_b;
      if (beregnet_bra !== bra) {
        const bekreft = window.confirm(
          `⚠️ Arealsjekk:\n\n` +
          `BRA-i + BRA-e + BRA-b = ${beregnet_bra} m²\n` +
          `Du har oppgitt BRA = ${bra} m²\n\n` +
          `Differanse: ${Math.abs(beregnet_bra - bra)} m²\n\n` +
          `Vil du fortsette med BRA = ${bra} m²?`
        );
        if (!bekreft) return;
      }
    }

    // Sjekk GUA = BRA + ALH
    if ((bra || alh) && gua) {
      const beregnet_gua = bra + alh;
      if (beregnet_gua !== gua) {
        const bekreft = window.confirm(
          `⚠️ Arealsjekk:\n\n` +
          `BRA + ALH = ${beregnet_gua} m²\n` +
          `Du har oppgitt GUA = ${gua} m²\n\n` +
          `Differanse: ${Math.abs(beregnet_gua - gua)} m²\n\n` +
          `Vil du fortsette med GUA = ${gua} m²?`
        );
        if (!bekreft) return;
      }
    }

    try {
      const data = {
        eiendom_id: parseInt(id),
        bygning_type: bygningForm.bygning_type,
        bygning_navn: bygningForm.bygning_navn,
        byggeaar: bygningForm.byggeaar ? parseInt(bygningForm.byggeaar) : null,
        bra: bygningForm.bra ? parseInt(bygningForm.bra) : null,
        bra_i: bygningForm.bra_i ? parseInt(bygningForm.bra_i) : null,
        bra_e: bygningForm.bra_e ? parseInt(bygningForm.bra_e) : null,
        bra_b: bygningForm.bra_b ? parseInt(bygningForm.bra_b) : null,
        tba: bygningForm.tba ? parseInt(bygningForm.tba) : null,
        gua: bygningForm.gua ? parseInt(bygningForm.gua) : null,
        alh: bygningForm.alh ? parseInt(bygningForm.alh) : null,
        etasjer: parseInt(bygningForm.etasjer),
      };

      await bygningAPI.create(data);
      setShowNyBygning(false);
      setBygningForm({
        bygning_type: 'hovedbygg',
        bygning_navn: '',
        byggeaar: '',
        bra: '',
        bra_i: '',
        bra_e: '',
        bra_b: '',
        tba: '',
        gua: '',
        alh: '',
        etasjer: 1,
      });
      fetchData();
    } catch (err) {
      setError('Kunne ikke opprette bygning');
      console.error(err);
    }
  };

  const handleRomSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        bygning_id: showNyRom,
        romtype: romForm.romtype,
        romnummer: parseInt(romForm.romnummer),
        etasje: romForm.etasje,
        tilstandsgrad: romForm.tilstandsgrad,
        renoverings_aar: romForm.renoverings_aar ? parseInt(romForm.renoverings_aar) : null,
      };

      const response = await romAPI.create(data);
      const nyttRomId = response.data.id;

      // Gå direkte til befaring for det nye rommet
      navigate(`/befaring/${nyttRomId}`);
    } catch (err) {
      setError('Kunne ikke opprette rom');
      console.error(err);
    }
  };

  const romtypeNavn = (type) => {
    const typer = {
      bad: 'Bad',
      wc: 'WC',
      vaskerom: 'Vaskerom',
      kjokken: 'Kjøkken',
      stue: 'Stue',
      soverom: 'Soverom',
      gang: 'Gang',
      bod: 'Bod',
      annet: 'Annet',
    };
    return typer[type] || type;
  };

  const etasjeNavn = (etasje) => {
    const etasjer = {
      kjeller: 'Kjeller',
      etasje_1: '1. etasje',
      etasje_2: '2. etasje',
      etasje_3: '3. etasje',
      loft: 'Loft',
    };
    return etasjer[etasje] || etasje;
  };

  if (loading) return <div className="loading">Laster...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!eiendom) return <div className="error-message">Eiendom ikke funnet</div>;

  return (
    <div className="eiendom-page">
      <nav className="navbar">
        <div className="navbar-content">
          <button onClick={() => navigate('/dashboard')} className="btn-back">
            ← Tilbake til dashboard
          </button>
          <h1>Eiendom</h1>
        </div>
      </nav>

      <div className="eiendom-content">
        <div className="eiendom-header">
          <div>
            <h2>{eiendom.adresse}</h2>
            <p className="eiendom-info">
              {eiendom.postnr} {eiendom.poststed}, {eiendom.kommune}
            </p>
            {eiendom.gnr && eiendom.bnr && (
              <p className="eiendom-info">
                Gnr {eiendom.gnr}, bnr {eiendom.bnr}
              </p>
            )}
            {eiendom.eier_navn && (
              <p className="eiendom-info">Eier: {eiendom.eier_navn}</p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => navigate(`/kontrollsenter/${id}`)}
              className="btn-secondary"
              style={{ backgroundColor: '#2196F3', color: 'white' }}
            >
              🎯 Kontrollsenter
            </button>
            <RapportKnapp eiendomId={id} navigate={navigate} />
            <button
              onClick={() => setShowNyBygning(!showNyBygning)}
              className="btn-primary"
            >
              + Ny bygning
            </button>
          </div>
        </div>

        {/* BILDESEKSJON: Eiendomsbilder */}
        <div className="bilde-seksjon" style={{marginTop: '30px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px'}}>
          <h3>📸 Bilder av eiendommen</h3>
          <p style={{color: '#666', fontSize: '14px'}}>
            Oversiktsbilder av tomt, adkomst, parkering, omgivelser
          </p>

          <BildeOpplaster
            type="eiendom"
            id={id}
            placeholder="Ta bilde av eiendommen"
            onUploadSuccess={fetchEiendomBilder}
          />

          <BildeGalleri
            bilder={eiendomBilder}
            onRefresh={fetchEiendomBilder}
          />
        </div>

        {showNyBygning && (
          <div className="form-card">
            <h3>Opprett ny bygning</h3>
            <form onSubmit={handleBygningSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Bygningstype *</label>
                  <select
                    value={bygningForm.bygning_type}
                    onChange={(e) =>
                      setBygningForm({ ...bygningForm, bygning_type: e.target.value })
                    }
                    required
                  >
                    <option value="hovedbygg">Hovedbygg</option>
                    <option value="garasje">Garasje</option>
                    <option value="uthus">Uthus</option>
                    <option value="anneks">Anneks</option>
                    <option value="annet">Annet</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Bygningsnavn</label>
                  <input
                    type="text"
                    value={bygningForm.bygning_navn}
                    onChange={(e) =>
                      setBygningForm({ ...bygningForm, bygning_navn: e.target.value })
                    }
                    placeholder="F.eks. Tomannsbolig"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Byggeår</label>
                  <input
                    type="number"
                    value={bygningForm.byggeaar}
                    onChange={(e) =>
                      setBygningForm({ ...bygningForm, byggeaar: e.target.value })
                    }
                    placeholder="1919"
                  />
                </div>
                <div className="form-group">
                  <label>Etasjer</label>
                  <input
                    type="number"
                    value={bygningForm.etasjer}
                    onChange={(e) =>
                      setBygningForm({ ...bygningForm, etasjer: e.target.value })
                    }
                    min="1"
                  />
                </div>
              </div>

              <h4 style={{marginTop: '20px', marginBottom: '10px', fontSize: '16px', color: '#333'}}>
                📏 Arealoppgaver (NS 3940:2023 / §2-20 NS3600:2025)
              </h4>
              <p style={{fontSize: '13px', color: '#666', marginBottom: '15px'}}>
                <strong>Lovpålagt:</strong> BRA totalt samt fordeling mellom BRA-i, BRA-e og BRA-b må oppgis.<br/>
                Alle areal oppgis i hele tall (m²).
              </p>

              {/* Steg 1: BRA-komponenter */}
              <div style={{marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
                <h5 style={{margin: '0 0 10px 0', color: '#333'}}>1️⃣ Bruksareal-komponenter (føres inn først)</h5>

                <div className="form-row">
                  <div className="form-group">
                    <label>BRA-i - Internt bruksareal (m²) 📋</label>
                    <input
                      type="number"
                      value={bygningForm.bra_i}
                      onChange={(e) =>
                        setBygningForm({ ...bygningForm, bra_i: e.target.value })
                      }
                      placeholder="Stue, soverom, kjøkken, etc."
                    />
                    <small style={{color: '#666', fontSize: '12px'}}>Areal innenfor omsluttende vegger</small>
                  </div>
                  <div className="form-group">
                    <label>BRA-e - Eksternt bruksareal (m²) 📋</label>
                    <input
                      type="number"
                      value={bygningForm.bra_e}
                      onChange={(e) =>
                        setBygningForm({ ...bygningForm, bra_e: e.target.value })
                      }
                      placeholder="Kjellerstue, anneks, garasje, etc."
                    />
                    <small style={{color: '#666', fontSize: '12px'}}>Tilleggsbygg, rom utenfor boenheten</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>BRA-b - Innglasset balkong/veranda (m²) 📋</label>
                    <input
                      type="number"
                      value={bygningForm.bra_b}
                      onChange={(e) =>
                        setBygningForm({ ...bygningForm, bra_b: e.target.value })
                      }
                      placeholder="Innglassede arealer"
                    />
                    <small style={{color: '#666', fontSize: '12px'}}>Innglasset balkong, veranda, altan</small>
                  </div>
                  <div className="form-group">
                    {/* Tom for å holde layout */}
                  </div>
                </div>
              </div>

              {/* Steg 2: BRA totalt (beregnes eller overstyres) */}
              <div style={{marginBottom: '20px', padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '5px', borderLeft: '4px solid #4caf50'}}>
                <h5 style={{margin: '0 0 10px 0', color: '#333'}}>2️⃣ BRA - Bruksareal totalt (genereres automatisk)</h5>

                <div className="form-row">
                  <div className="form-group">
                    <label>BRA - Bruksareal totalt (m²) 📋</label>
                    <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                      <input
                        type="number"
                        value={bygningForm.bra}
                        onChange={(e) =>
                          setBygningForm({ ...bygningForm, bra: e.target.value })
                        }
                        placeholder="Klikk 'Beregn BRA' eller skriv inn manuelt"
                        style={{
                          flex: 1,
                          borderColor: (() => {
                            const bra_i = parseInt(bygningForm.bra_i) || 0;
                            const bra_e = parseInt(bygningForm.bra_e) || 0;
                            const bra_b = parseInt(bygningForm.bra_b) || 0;
                            const bra = parseInt(bygningForm.bra) || 0;
                            const beregnet = bra_i + bra_e + bra_b;
                            if ((bra_i || bra_e || bra_b) && bra && beregnet !== bra) return '#ff9800';
                            return undefined;
                          })()
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const bra_i = parseInt(bygningForm.bra_i) || 0;
                          const bra_e = parseInt(bygningForm.bra_e) || 0;
                          const bra_b = parseInt(bygningForm.bra_b) || 0;
                          setBygningForm({ ...bygningForm, bra: (bra_i + bra_e + bra_b).toString() });
                        }}
                        style={{
                          padding: '8px 15px',
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        🧮 Beregn BRA
                      </button>
                    </div>
                    <small style={{color: '#666', fontSize: '12px'}}>
                      BRA = BRA-i + BRA-e + BRA-b
                      {(() => {
                        const bra_i = parseInt(bygningForm.bra_i) || 0;
                        const bra_e = parseInt(bygningForm.bra_e) || 0;
                        const bra_b = parseInt(bygningForm.bra_b) || 0;
                        const beregnet = bra_i + bra_e + bra_b;
                        if (bra_i || bra_e || bra_b) {
                          return ` → Beregnet: ${beregnet} m²`;
                        }
                        return '';
                      })()}
                    </small>
                  </div>
                </div>
              </div>

              {/* Steg 3: Tilleggsarealer */}
              <div style={{marginBottom: '20px'}}>
                <h5 style={{margin: '0 0 10px 0', color: '#333'}}>3️⃣ Tilleggsarealer (valgfritt)</h5>

                <div className="form-row">
                  <div className="form-group">
                    <label>TBA - Terrasse/balkong åpent (m²) ✨</label>
                    <input
                      type="number"
                      value={bygningForm.tba}
                      onChange={(e) =>
                        setBygningForm({ ...bygningForm, tba: e.target.value })
                      }
                      placeholder="Åpne arealer"
                    />
                    <small style={{color: '#666', fontSize: '12px'}}>Terrasser, åpne altaner og balkonger</small>
                  </div>
                  <div className="form-group">
                    <label>ALH - Areal lav himlingshøyde (m²) ✨</label>
                    <input
                      type="number"
                      value={bygningForm.alh}
                      onChange={(e) =>
                        setBygningForm({ ...bygningForm, alh: e.target.value })
                      }
                      placeholder="Areal under skråtak"
                    />
                    <small style={{color: '#666', fontSize: '12px'}}>Areal som ikke teller som BRA pga. lav takhøyde</small>
                  </div>
                </div>
              </div>

              {/* Steg 4: GUA (kun hvis ALH er oppgitt) */}
              {(parseInt(bygningForm.alh) > 0 || bygningForm.gua) && (
                <div style={{marginBottom: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '5px', borderLeft: '4px solid #ffc107'}}>
                  <h5 style={{margin: '0 0 10px 0', color: '#333'}}>4️⃣ GUA - Gulvareal totalt (for boliger med skråtak)</h5>

                  <div className="form-row">
                    <div className="form-group">
                      <label>GUA - Gulvareal totalt (m²) ✨</label>
                      <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                        <input
                          type="number"
                          value={bygningForm.gua}
                          onChange={(e) =>
                            setBygningForm({ ...bygningForm, gua: e.target.value })
                          }
                          placeholder="Klikk 'Beregn GUA' eller skriv inn manuelt"
                          style={{
                            flex: 1,
                            borderColor: (() => {
                              const bra = parseInt(bygningForm.bra) || 0;
                              const alh = parseInt(bygningForm.alh) || 0;
                              const gua = parseInt(bygningForm.gua) || 0;
                              const beregnet = bra + alh;
                              if ((bra || alh) && gua && beregnet !== gua) return '#ff9800';
                              return undefined;
                            })()
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const bra = parseInt(bygningForm.bra) || 0;
                            const alh = parseInt(bygningForm.alh) || 0;
                            setBygningForm({ ...bygningForm, gua: (bra + alh).toString() });
                          }}
                          style={{
                            padding: '8px 15px',
                            backgroundColor: '#ffc107',
                            color: '#333',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          🧮 Beregn GUA
                        </button>
                      </div>
                      <small style={{color: '#666', fontSize: '12px'}}>
                        For boliger med skråtak (GUA = BRA + ALH)
                        {(() => {
                          const bra = parseInt(bygningForm.bra) || 0;
                          const alh = parseInt(bygningForm.alh) || 0;
                          const beregnet = bra + alh;
                          if (bra || alh) {
                            return ` → Beregnet: ${beregnet} m²`;
                          }
                          return '';
                        })()}
                      </small>
                    </div>
                  </div>
                </div>
              )}

              <p style={{fontSize: '12px', color: '#666', marginTop: '10px', fontStyle: 'italic'}}>
                📋 = Lovpålagt (NS3600 / § 2-18 Tryggere bolighandel) | ✨ = Anbefalt (Eiendomsmeglingsforskriften)
              </p>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Opprett bygning
                </button>
                <button
                  type="button"
                  onClick={() => setShowNyBygning(false)}
                  className="btn-secondary"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bygninger-section">
          <h3>Bygninger ({bygninger.length})</h3>

          {bygninger.length === 0 ? (
            <div className="empty-state">
              <p>Ingen bygninger ennå</p>
              <p>Klikk "Ny bygning" for å komme i gang</p>
            </div>
          ) : (
            <div className="bygninger-list">
              {bygninger.map((bygning) => (
                <div key={bygning.id} className="bygning-card">
                  <div className="bygning-header">
                    <h4>
                      {bygning.bygning_navn || bygning.bygning_type}
                      {bygning.byggeaar && ` (${bygning.byggeaar})`}
                    </h4>
                    <button
                      onClick={() => setShowNyRom(bygning.id)}
                      className="btn-small"
                    >
                      + Legg til rom
                    </button>
                  </div>

                  {bygning.bra && (
                    <p className="bygning-info">
                      BRA: {bygning.bra} m² {bygning.bra_i && `(BRA-i: ${bygning.bra_i} m²)`}
                    </p>
                  )}

                  {/* BILDESEKSJON: Bygningsbilder */}
                  <div className="bilde-seksjon" style={{marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '6px'}}>
                    <h4>📸 Bilder av {bygning.bygning_navn || bygning.bygning_type}</h4>
                    <p style={{color: '#666', fontSize: '13px'}}>
                      Fasader (alle sider), tak, grunnmur
                    </p>

                    <BildeOpplaster
                      type="bygning"
                      id={bygning.id}
                      placeholder="Ta bilde av bygningen"
                      onUploadSuccess={() => fetchBygningBilder(bygning.id)}
                    />

                    <BildeGalleri
                      bilder={bygningBilder[bygning.id] || []}
                      onRefresh={() => fetchBygningBilder(bygning.id)}
                    />
                  </div>

                  {showNyRom === bygning.id && (
                    <div className="rom-form">
                      <h5>Nytt rom</h5>
                      <form onSubmit={handleRomSubmit}>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Romtype *</label>
                            <select
                              value={romForm.romtype}
                              onChange={(e) =>
                                setRomForm({ ...romForm, romtype: e.target.value })
                              }
                              required
                            >
                              <option value="bad">Bad</option>
                              <option value="wc">WC</option>
                              <option value="vaskerom">Vaskerom</option>
                              <option value="kjokken">Kjøkken</option>
                              <option value="stue">Stue</option>
                              <option value="soverom">Soverom</option>
                              <option value="gang">Gang</option>
                              <option value="bod">Bod</option>
                              <option value="annet">Annet</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Romnummer</label>
                            <input
                              type="number"
                              value={romForm.romnummer}
                              onChange={(e) =>
                                setRomForm({ ...romForm, romnummer: e.target.value })
                              }
                              min="1"
                            />
                          </div>
                          <div className="form-group">
                            <label>Etasje *</label>
                            <select
                              value={romForm.etasje}
                              onChange={(e) =>
                                setRomForm({ ...romForm, etasje: e.target.value })
                              }
                              required
                            >
                              <option value="kjeller">Kjeller</option>
                              <option value="etasje_1">1. etasje</option>
                              <option value="etasje_2">2. etasje</option>
                              <option value="etasje_3">3. etasje</option>
                              <option value="loft">Loft</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Tilstandsgrad</label>
                            <select
                              value={romForm.tilstandsgrad}
                              onChange={(e) =>
                                setRomForm({ ...romForm, tilstandsgrad: e.target.value })
                              }
                            >
                              <option value="TG0">TG0 - Som nytt</option>
                              <option value="TG1">TG1 - Meget god</option>
                              <option value="TG2">TG2 - God</option>
                              <option value="TG3">TG3 - Dårlig</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Renoveringsår (valgfritt)</label>
                            <input
                              type="number"
                              value={romForm.renoverings_aar}
                              onChange={(e) =>
                                setRomForm({ ...romForm, renoverings_aar: e.target.value })
                              }
                              placeholder="F.eks. 2011"
                              min="1900"
                              max={new Date().getFullYear()}
                            />
                            <small className="form-hint">
                              Hvis rommet er renovert etter byggeår. Brukes for TEK-vurdering.
                            </small>
                          </div>
                        </div>
                        <div className="form-actions">
                          <button type="submit" className="btn-primary btn-small">
                            Opprett rom
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowNyRom(null)}
                            className="btn-secondary btn-small"
                          >
                            Avbryt
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  <div className="rom-list">
                    <h5>Rom ({bygning.rom?.length || 0})</h5>
                    {bygning.rom?.length > 0 ? (
                      <div className="rom-grid">
                        {bygning.rom.map((rom) => (
                          <div
                            key={rom.id}
                            className="rom-card"
                            onClick={() => navigate(`/befaring/${rom.id}`)}
                          >
                            <div className="rom-header">
                              <span className="rom-type">
                                {romtypeNavn(rom.romtype)} {rom.romnummer}
                              </span>
                              <span className={`tg-badge tg-${rom.tilstandsgrad?.toLowerCase()}`}>
                                {rom.tilstandsgrad || 'Ikke vurdert'}
                              </span>
                            </div>
                            <p className="rom-etasje">{etasjeNavn(rom.etasje)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="empty-rom">Ingen rom ennå</p>
                    )}
                  </div>

                  {/* Bygningsdeler §2-18 sjekkliste */}
                  <BygningsdelSjekkliste bygningId={bygning.id} navigate={navigate} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Bygningsdel-sjekkliste komponent
function BygningsdelSjekkliste({ bygningId, navigate }) {
  const [bygningsdeler, setBygningsdeler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInitialisering, setShowInitialisering] = useState(false);

  const OBLIGATORISKE_BYGNINGSDELER = [
    { type: 'yttertak', navn: 'Yttertak', ikon: '🏠' },
    { type: 'yttervegger', navn: 'Yttervegger', ikon: '🧱' },
    { type: 'vinduer_dorer', navn: 'Vinduer og dører', ikon: '🚪' },
    { type: 'fundament_sokkel', navn: 'Fundament og sokkel', ikon: '⚓' },
    { type: 'drenering', navn: 'Drenering', ikon: '💧' },
    { type: 'elektrisk', navn: 'Elektrisk anlegg', ikon: '⚡' },
    { type: 'vvs', navn: 'VVS-anlegg', ikon: '🚰' },
    { type: 'ventilasjon', navn: 'Ventilasjon', ikon: '💨' },
    { type: 'brannsikkerhet', navn: 'Brannsikkerhet', ikon: '🔥' },
  ];

  useEffect(() => {
    fetchBygningsdeler();
  }, [bygningId]);

  const fetchBygningsdeler = async () => {
    try {
      const response = await bygningsdelAPI.getByBygning(bygningId);
      setBygningsdeler(response.data);
    } catch (err) {
      console.error('Kunne ikke hente bygningsdeler:', err);
    } finally {
      setLoading(false);
    }
  };

  const initialiserBygningsdeler = async () => {
    try {
      setShowInitialisering(true);
      for (const del of OBLIGATORISKE_BYGNINGSDELER) {
        await bygningsdelAPI.create({
          bygning_id: bygningId,
          bygningsdel_type: del.type,
          bygningsdel_navn: del.navn,
        });
      }
      await fetchBygningsdeler();
      setShowInitialisering(false);
    } catch (err) {
      console.error('Kunne ikke initialisere bygningsdeler:', err);
      alert('Feil ved initialisering av bygningsdeler');
      setShowInitialisering(false);
    }
  };

  const getStatusInfo = (bygningsdel) => {
    if (!bygningsdel) {
      return {
        className: 'status-ikke-pabegynt',
        tekst: 'Ikke påbegynt',
        farge: '#999',
        bakgrunn: '#f5f5f5',
        borderFarge: '#ddd'
      };
    }
    switch (bygningsdel.status) {
      case 'fullfort':
        return {
          className: 'status-fullfort',
          tekst: '✓ Fullført',
          farge: '#2e7d32',
          bakgrunn: '#e8f5e9',
          borderFarge: '#4caf50'
        };
      case 'pabegynt':
        return {
          className: 'status-pabegynt',
          tekst: '⚠️ Mangler',
          farge: '#f57c00',
          bakgrunn: '#fff3e0',
          borderFarge: '#ff9800'
        };
      case 'under_arbeid':
        return {
          className: 'status-under-arbeid',
          tekst: '⏳ Under arbeid',
          farge: '#ff9800',
          bakgrunn: '#fff3e0',
          borderFarge: '#ffa726'
        };
      case 'ikke_pabegynt':
      default:
        return {
          className: 'status-ikke-pabegynt',
          tekst: 'Ikke påbegynt',
          farge: '#c62828',
          bakgrunn: '#ffebee',
          borderFarge: '#f44336'
        };
    }
  };

  if (loading) {
    return <div style={{ padding: '10px', color: '#666' }}>Laster bygningsdeler...</div>;
  }

  // Hvis ingen bygningsdeler, vis knapp for å initialisere
  if (bygningsdeler.length === 0) {
    return (
      <div className="bygningsdel-sjekkliste" style={{ marginTop: '20px' }}>
        <h5>Bygningsdeler (§2-18 Forskrift om tryggere bolighandel)</h5>
        <div style={{
          padding: '20px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          marginTop: '10px'
        }}>
          <p style={{ margin: '0 0 15px 0', color: '#856404' }}>
            ⚠️ Bygningsdelene er ikke initialisert ennå. Klikk under for å opprette sjekkliste basert på §2-18.
          </p>
          <button
            onClick={initialiserBygningsdeler}
            disabled={showInitialisering}
            className="btn-primary btn-small"
          >
            {showInitialisering ? 'Initialiserer...' : 'Initialiser §2-18 sjekkliste'}
          </button>
        </div>
      </div>
    );
  }

  // Vis sjekkliste med status
  const fullfortCount = bygningsdeler.filter(b => b.status === 'fullfort').length;
  const totaltCount = OBLIGATORISKE_BYGNINGSDELER.length;
  const prosent = Math.round((fullfortCount / totaltCount) * 100);

  return (
    <div className="bygningsdel-sjekkliste" style={{ marginTop: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h5>Bygningsdeler (§2-18)</h5>
        <span style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: prosent === 100 ? '#4caf50' : '#ff9800'
        }}>
          {fullfortCount}/{totaltCount} ({prosent}%)
        </span>
      </div>

      <div style={{
        height: '6px',
        backgroundColor: '#e0e0e0',
        borderRadius: '3px',
        overflow: 'hidden',
        marginBottom: '15px'
      }}>
        <div style={{
          height: '100%',
          width: `${prosent}%`,
          backgroundColor: prosent === 100 ? '#4caf50' : '#ff9800',
          transition: 'width 0.3s ease'
        }} />
      </div>

      <div style={{ display: 'grid', gap: '8px' }}>
        {OBLIGATORISKE_BYGNINGSDELER.map((obligatorisk) => {
          const bygningsdel = bygningsdeler.find(b => b.bygningsdel_type === obligatorisk.type);
          const statusInfo = getStatusInfo(bygningsdel);

          return (
            <div
              key={obligatorisk.type}
              onClick={() => {
                if (bygningsdel) {
                  navigate(`/bygningsdel-befaring/${bygningsdel.id}`);
                }
              }}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                border: `2px solid ${statusInfo.borderFarge}`,
                borderRadius: '4px',
                backgroundColor: statusInfo.bakgrunn,
                cursor: bygningsdel ? 'pointer' : 'not-allowed',
                opacity: bygningsdel ? 1 : 0.6,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (bygningsdel) {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>{obligatorisk.ikon}</span>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>{obligatorisk.navn}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontSize: '13px',
                  color: statusInfo.farge,
                  fontWeight: '500'
                }}>
                  {statusInfo.tekst}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Rapport-knapp - Alltid tilgjengelig for foreløpig rapport
function RapportKnapp({ eiendomId, navigate }) {
  const [validering, setValidering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    fetchValidering();
  }, [eiendomId]);

  const fetchValidering = async () => {
    try {
      const response = await rapportAPI.valider(eiendomId);
      setValidering(response.data);
    } catch (err) {
      console.error('Kunne ikke validere:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRapportKlikk = () => {
    if (!validering) {
      fetchValidering();
      return;
    }

    // Hvis ikke klar, vis advarsel først
    if (!validering.erKlar) {
      setShowInfoModal(true);
    } else {
      // Gå direkte til rapport hvis klar
      navigate(`/rapport/${eiendomId}`);
    }
  };

  const handleGaaTilRapport = () => {
    setShowInfoModal(false);
    navigate(`/rapport/${eiendomId}`);
  };

  if (loading) {
    return (
      <button className="btn-secondary" disabled>
        Laster...
      </button>
    );
  }

  const btnStyle = validering?.erKlar
    ? { backgroundColor: '#4caf50' }
    : { backgroundColor: '#ff9800' };

  return (
    <>
      <button
        onClick={handleRapportKlikk}
        className="btn-primary"
        style={btnStyle}
      >
        📄 Generer foreløpig rapport {validering && `(${validering.prosent}%)`}
      </button>

      {showInfoModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowInfoModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>📋 Generer foreløpig rapport?</h2>
            <p>
              Befaringen er ikke fullført ({validering.prosent}%). Du kan likevel generere en foreløpig rapport som vil bli tydelig merket som <strong>"FORELØPIG RAPPORT"</strong>.
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
              Dette er nyttig for å se fremdrift eller dele statusrapport underveis.
            </p>

            <div style={{ marginTop: '20px' }}>
              <div style={{
                padding: '15px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                marginBottom: '15px'
              }}>
                <strong>Progresjon: {validering.fullforteBygningsdeler}/{validering.totaltAntallBygningsdeler} fullført ({validering.prosent}%)</strong>
              </div>

              {validering.feil && validering.feil.length > 0 && (
                <div style={{ marginTop: '15px' }}>
                  <h4>Manglende inspeksjoner:</h4>
                  <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                    {validering.feil.map((f, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>
                        <strong>{f.bygning}:</strong> {f.feil}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowInfoModal(false)}
                className="btn-secondary"
              >
                Avbryt
              </button>
              <button
                onClick={handleGaaTilRapport}
                className="btn-primary"
                style={{ backgroundColor: '#ff9800' }}
              >
                Generer foreløpig rapport
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Eiendom;
